import * as React from "react";

import { StatusPill } from "@/components/ui/status-pill";
import { cn } from "@/lib/utils";

/** @experimental No backing API field yet — do not wire to live data until a product spec is published. */
export type TrustState = "verified" | "unverified" | "hallucinated";

export interface SourceIdentityProps {
  title: string;
  trustState?: TrustState;
  /** Scale controls density: `dot` is minimal, `row` is list-sized, `card` is full. */
  scale?: "dot" | "row" | "card";
  className?: string;
}

const trustVariant: Record<TrustState, "success" | "neutral" | "destructive"> = {
  verified: "success",
  unverified: "neutral",
  hallucinated: "destructive",
};

const trustLabel: Record<TrustState, string> = {
  verified: "Verified",
  unverified: "Unverified",
  hallucinated: "Hallucinated",
};

const SourceIdentity = React.forwardRef<HTMLSpanElement, SourceIdentityProps>(
  ({ title, trustState = "unverified", scale = "row", className }, ref) => {
    const variant = trustVariant[trustState];

    if (scale === "dot") {
      return (
        <span ref={ref} className={cn("inline-flex items-center gap-1.5", className)} title={title}>
          <span
            className={cn("size-2 shrink-0 rounded-full", {
              "bg-success": trustState === "verified",
              "bg-muted-foreground": trustState === "unverified",
              "bg-destructive": trustState === "hallucinated",
            })}
            aria-hidden
          />
          <span className="truncate text-xs text-muted-foreground">{title}</span>
        </span>
      );
    }

    // row (inline) and card (block with larger text)
    const isCard = scale === "card";
    return (
      <span
        ref={ref}
        className={cn(
          isCard ? "flex min-w-0 items-start gap-3" : "inline-flex min-w-0 items-center gap-2",
          className
        )}
      >
        <span className={cn("truncate font-medium", isCard ? "text-base" : "text-sm")}>{title}</span>
        <StatusPill variant={variant} className="shrink-0">{trustLabel[trustState]}</StatusPill>
      </span>
    );
  }
);
SourceIdentity.displayName = "SourceIdentity";

export { SourceIdentity };
